const YAML_PATH_PATTERN = /\/[\w\-/\d]+/;

type YamlPath = {
  key: string | undefined;
  // Number of non-numeric path segments — used to determine expected indentation depth.
  // "/options" → depth 1 (top-level), "/stacks/0/vcs/repository" → depth 3
  depth: number;
};

// Extracts the last meaningful (non-numeric) key and its depth from a YAML path in a message.
export function parseYamlPath(message: string): YamlPath {
  const match = message.match(YAML_PATH_PATTERN);
  if (!match) return { key: undefined, depth: 0 };

  const nonNumericSegments = match[0]
    .split("/")
    .filter((s) => s.length > 0 && !/^\d+$/.test(s));

  return { key: nonNumericSegments.at(-1), depth: nonNumericSegments.length };
}

// Finds the 1-indexed line number of "key:" in YAML content.
// For depth=1 (top-level key), anchors to line start to avoid matching nested keys with the same name.
// Returns 1 as fallback if not found.
export function findKeyLine(yamlContent: string, key: string, depth: number): number {
  const lines = yamlContent.split("\n");
  const pattern = depth === 1 ? new RegExp(`^${key}\\s*:`) : new RegExp(`\\b${key}\\s*:`);
  const index = lines.findIndex((line) => pattern.test(line));
  return index === -1 ? 1 : index + 1;
}

function stripLineComment(line: string): string {
  let inSingle = false;
  let inDouble = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === "'" && !inDouble) inSingle = !inSingle;
    if (char === '"' && !inSingle) inDouble = !inDouble;
    if (char === "#" && !inSingle && !inDouble) {
      return line.slice(0, i).trimEnd();
    }
  }
  return line;
}

// Strips YAML comments from a YAML string.
// Preserves structural blank lines; removes comment-only lines entirely.
export function stripYamlComments(yaml: string): string {
  const result: string[] = [];
  for (const line of yaml.split("\n")) {
    const stripped = stripLineComment(line);
    if (stripped.trim().length > 0 || line.trim().length === 0) {
      result.push(stripped);
    }
  }
  return result.join("\n");
}
